import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Theme, useMediaQuery, useTheme } from '@mui/material'
import _ from 'lodash'
import React, { createContext, useContext } from 'react'
import {
  FormProvider as HookProvider,
  UseFormReturn,
  useForm,
  useWatch,
} from 'react-hook-form'
import useDoGetProducts from '../hooks/useDoGetProducts'
import useGetLenders from '../hooks/useGetLenders'
import { useDrawerStore } from '../store/useDrawerStore'
import { FormSchema, formSchema } from '../types/form'
import { Lender, Product } from '../types/product'
import { formatDate } from '../utils/date'

type FormContextValue = {
  form: UseFormReturn<FormSchema>
  isFetchingLenders: boolean
  isFetchingProducts: boolean
  isFetchingProductsSuccess: boolean
  lenders: Lender[]
  productsList: Product[]
  onSubmit: (values: FormSchema) => void
}

const otherFeatures = [
  'Offset',
  'Flexible (No ERC)',
  'Help to Buy',
  'First Time Buyer',
  'New Build',
]
const FormContext = createContext<FormContextValue | undefined>(undefined)

export const useFormContext = (): FormContextValue => {
  const context = useContext(FormContext)
  if (!context) {
    throw new Error('useFormContext must be used within a FormProvider')
  }
  return context
}

// FormProvider component
export const FormProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const theme: Theme & { custom: any } = useTheme()
  const { setDrawerToggle, isDrawerOpen } = useDrawerStore()
  const isMatchDownMd = useMediaQuery(theme.breakpoints.down('md'))
  //@ts-ignore
  const config = theme?.custom?.appConfig

  const { data: lenders, isFetching } = useGetLenders(theme?.custom?.apiKey)
  const {
    data: products,
    mutateAsync,
    isPending,
    isSuccess,
  } = useDoGetProducts()

  const [productsList, setProductsList] = React.useState<Product[]>([])

  // Initialize React Hook Form
  const form = useForm<FormSchema>({
    defaultValues: {
      paymentMethod: config?.paymentMethod ?? 'Repayment',
      propertyValue: config?.propertyValue
        ? parseInt(config?.propertyValue)
        : 200000,
      loanRequired: config?.loanRequired
        ? parseInt(config?.loanRequired)
        : 120000,
      term: config?.term ? parseInt(config?.term) : 25,
      businessType: config?.businessType ?? 'Standard Purchase',
      rent: config?.rent ? parseInt(config?.rent) : 0,
      mortgageClass: config?.mortgageClass ?? '',
      initialPeriod: config?.initialPeriod ?? '',
      lender: '',
      region: config?.region ?? '',
      sortedBy: 'InitialPayRate',
      discountedPurchasePrice: config?.discountedPurchasePrice
        ? parseInt(config?.discountedPurchasePrice)
        : 0,
      shareValue: config?.shareValue ? parseInt(config?.shareValue) : 0,
      percentagePurchased: config?.percentagePurchased
        ? parseInt(config?.percentagePurchased)
        : 0,
      others: config?.others || '',
      fees: config?.fees ?? '',
      buildingUse: config?.buildingUse ?? 'No_Selection',
      creditQuestions: config?.creditQuestions
        ? config?.creditQuestions.split(',')
        : [],
    },
    resolver: zodResolver(formSchema),
  })
  const formValues = form.getValues()

  const sortedBy = useWatch({ control: form.control, name: 'sortedBy' })

  const DEFAULT_LENDERS_TO_REMOVE =
    'Central Trust Limited,CHL Mortgages,Cumberland Building Society,Earl Shilton BS,Equifinance,Hampshire Trust Bank,Harpenden BS,Just,Legal & General Home Finance,Lendco,Loughborough BS,MFS – Market Financial Solutions,Molo Finance,MT Finance,Nomo,Norton Home Loans,Octopus Real Estate,Perenna,Reliance Bank,Selina Finance,State Bank of India UK,Step One Finance,West Bromwich Commercial Limited,Yorkshire Building Society'
  const removedLenders = config?.removedlenders
    ? config?.removedlenders?.concat(DEFAULT_LENDERS_TO_REMOVE).split(',')
    : DEFAULT_LENDERS_TO_REMOVE.split(',')
  const filteredLenders =
    lenders?.filter((lender) => !removedLenders.includes(lender.Name)) || []

  const getMortgageClassFilter = (
    formValues: FormSchema,
    mortgageClass: string
  ) => {
    if (!formValues?.mortgageClass || formValues?.mortgageClass.length === 0) {
      return 'No_Filter'
    }

    if (formValues?.mortgageClass === mortgageClass) {
      return 'Include'
    }

    return 'Ignore'
  }

  const modifyFormValues = (values: FormSchema) => {
    if (theme.custom.appConfig.isOffset === 'Yes') {
      values.others = 'Offset'
    } else if (theme.custom.appConfig.flexible === 'Yes') {
      values.others = 'Flexible (No ERC)'
    } else if (theme.custom.appConfig.helpToBuy === 'Yes') {
      values.others = 'Help to Buy'
    } else if (theme.custom.appConfig.firstTimeBuyer === 'Yes') {
      values.others = 'First Time Buyer'
    } else if (theme.custom.appConfig.newBuild === 'Yes') {
      values.others = 'New Build'
    }

    if (theme.custom.appConfig.cashback === 'Yes') {
      values.cashback = 'Cashback'
    }

    if (theme.custom.appConfig.buyToLetHMO === 'Yes') {
      values.businessType = 'Buy_To_Let Purchase'
      values.buildingUse = 'HMO'
    } else if (theme.custom.appConfig.limitedCompany === 'Yes') {
      values.businessType = 'Buy_To_Let Purchase'
      values.buildingUse = 'Ltd Company'
    } else if (theme.custom.appConfig.adverseCredit === 'Yes') {
      values.businessType = 'Bad_Credit Purchase'
      values.creditQuestions = ['CCJs', 'Arrears']
    } else if (theme.custom.appConfig.adverseCreditRemortgage === 'Yes') {
      values.businessType = 'Bad_Credit Remortgage'
      values.creditQuestions = ['CCJs', 'Arrears']
    }

    return values
  }

  const getOtherFeaturesFilter = (
    formValues: FormSchema,
    otherFeatures: string
  ) => {
    if (formValues?.others === otherFeatures) {
      return 'Include'
    }

    return 'No_Filter'
  }

  const getProductTermFilter = (initialPeriod: number) => {
    //filtering for 5 years

    // if (initialPeriod === 5) {
    //   return {
    //     ProductTermPeriodFromMonths: 50,
    //     ProductTermPeriodToMonths: 70,
    //   }
    // }

    // if (initialPeriod === 3) {
    //   return {
    //     ProductTermPeriodFromMonths: 24,
    //     ProductTermPeriodToMonths: 50,
    //   }
    // }

    // if (initialPeriod === 2) {
    //   return {
    //     ProductTermPeriodFromMonths: 1,
    //     ProductTermPeriodToMonths: 24,
    //   }
    // }

    return {
      ProductTermPeriodFromMonths: initialPeriod * 12,
      ProductTermPeriodToMonths: initialPeriod * 12,
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getRunSourceArgument = (values: FormSchema = formValues) => {
    if (config.showMenuBar === 'No') {
      values = modifyFormValues(values)
    }

    const isOffsetFilter = values?.others?.includes('Offset')
      ? {
          IsOffset: getOtherFeaturesFilter(values, 'Offset'),
        }
      : {}
    const isFlexible = values?.others?.includes('Flexible (No ERC)')
      ? {
          NoERCPayable: getOtherFeaturesFilter(values, 'Flexible (No ERC)'),
        }
      : {}
    const isHelpToBuy = values?.others?.includes('Help to Buy')
      ? {
          HelpToBuy: getOtherFeaturesFilter(values, 'Help to Buy'),
        }
      : {}
    const isFirstHome = values?.others?.includes('First Time Buyer')
      ? {
          Applicant1: {
            ApplicantType: 'First_Time_Buyer',
          },
        }
      : {}
    const isNewBuild =
      values?.others === 'New Build'
        ? {
            NewBuildDetails: {
              NumberOfMonths: 12,
            },
          }
        : {}
    const isCashback =
      values?.cashback === 'Cashback'
        ? {
            Cashback: 'Include',
          }
        : {}
    const isBadCredit = values?.businessType?.includes('Bad_Credit')
      ? {
          Applicant1: {
            ...isFirstHome?.Applicant1,
            CreditHistoryDetails: {
              // Bankcruptcies: [
              //   {
              //     Bankcruptcy: {
              //       Discharged: formatDate(
              //         new Date(new Date().setMonth(new Date().getMonth() - 3))
              //       ),
              //     },
              //   },
              // ],
              ...(values?.creditQuestions?.includes('Defaults') && {
                Defaults: [
                  {
                    Default: {
                      DateRegistered: formatDate(
                        new Date(new Date().setMonth(new Date().getMonth() - 3))
                      ),
                      DateSatisfied: formatDate(new Date()),
                    },
                  },
                ],
              }),
              ...(values?.creditQuestions?.includes('CCJs') && {
                CCJs: [
                  {
                    CCJ: {
                      DateRegistered: formatDate(
                        new Date(new Date().setMonth(new Date().getMonth() - 3))
                      ),
                      DateSatisfied: formatDate(new Date()),
                    },
                  },
                ],
              }),
              ...(values?.creditQuestions?.includes('IVAs') && {
                IVAs: [
                  {
                    IVA: {
                      DateRegistered: formatDate(
                        new Date(new Date().setMonth(new Date().getMonth() - 3))
                      ),
                      DateCompleted: formatDate(new Date()),
                      SatisfactoryConduct: true,
                    },
                  },
                ],
              }),
              ...(values?.creditQuestions?.includes('Arrears') && {
                Arrears: [
                  {
                    Arrear: {
                      DateOfArrear: formatDate(
                        new Date(new Date().setMonth(new Date().getMonth() - 3))
                      ),
                      DateCleared: formatDate(new Date()),
                    },
                  },
                ],
              }),
            },
          },
        }
      : {}
    return {
      Term: values?.term,
      ExpectedValuation: values?.propertyValue,
      LoanRequired: values?.loanRequired,
      PaymentMethod: values?.paymentMethod,
      ReasonForMortgage: values?.businessType?.split(' ')[1],
      MortgageType:
        values?.businessType?.split(' ')[0] === 'Bad_Credit'
          ? 'Standard'
          : values?.businessType?.split(' ')[0],
      MortgageClass: {
        LiborLinked: 'Ignore',
        Capped: 'Ignore',
        Fixed: getMortgageClassFilter(values, 'Fixed'),
        Variable: getMortgageClassFilter(values, 'Variable'),
        Discount: getMortgageClassFilter(values, 'Discount'),
        Tracker: getMortgageClassFilter(values, 'Tracker'),
      },
      ...(values?.businessType?.includes('Right_To_Buy') && {
        RightToBuyDetails: {
          DiscountedPurchasePrice: values?.discountedPurchasePrice,
        },
      }),
      ...isNewBuild,
      // ...(values?.others?.includes('New Build') && {
      //   NewBuildDetails: {
      //     NumberOfMonths: 1,
      //   },
      // }),
      ...isFirstHome,
      ...isBadCredit,
      ...(values?.businessType?.includes('Buy_To_Let') && {
        BuyToLetDetails: {
          ExpectedRentalIncomePerMonth: values?.rent,
          ...(values?.buildingUse !== 'Ltd Company' &&
            values?.buildingUse !== 'Holiday_Let' && {
              BuildingUse: values?.buildingUse || 'No_Selection',
            }),
          ...(values?.buildingUse === 'Ltd Company' && {
            LimitedCompanySPV: 'Yes',
          }),
        },
      }),
      ...(values?.businessType?.includes('Shared_Ownership') && {
        SharedOwnershipDetails: {
          ApplicantsShareOfValueAmount: values?.shareValue,
          ApplicantsShareOfValuePercentage: values?.percentagePurchased,
        },
      }),
      ...((values?.region ||
        (values?.businessType?.includes('Buy_To_Let') &&
          values?.buildingUse === 'Holiday_Let') ||
        values?.mortgageClass === 'New Build') && {
        PropertyDetails: {
          ...(values?.region && {
            PostCode: values?.region,
          }),
          ...(values?.businessType?.includes('Buy_To_Let') &&
            values?.buildingUse === 'Holiday_Let' && {
              PropertyUse: values?.buildingUse,
            }),
          ...(values?.mortgageClass === 'New Build' && {
            PropertyType: 'House',
          }),
        },
      }),
      ...((values?.fees === 'Add fees' || values?.fees === 'Pay Fees') && {
        TrueCostDetails: {
          AddFeesPayableToTruecostTotal:
            values?.fees === 'Pay Fees' ? false : true,
        },
      }),
      Filters: {
        ...isOffsetFilter,
        ...isFlexible,
        ...isHelpToBuy,
        ...isCashback,
        SortResultsByColumn: {
          Direction: 'Ascending',
          Column: 'InitialPayRate',
        },
        ...(values?.initialPeriod &&
          getProductTermFilter(parseInt(values?.initialPeriod))),
        Retention: 'Ignore',
        ...(filteredLenders?.length &&
          !values?.lender && {
            IncludedLenders: filteredLenders.map((lender) => ({
              string: lender.Code,
            })),
          }),
        ...(values?.lender && {
          IncludedLenders: [
            {
              string: values?.lender,
            },
          ],
        }),
        ...(values?.fees === 'No fees' && {
          NoArrangementFee: 'Include',
          NoBookingFee: 'Include',
        }),
        ...(values?.businessType?.includes('Bad_Credit') && {
          ...(values?.creditQuestions?.includes('Defaults') && {
            DefaultsAcceptable: 'Include',
          }),
          ...(values?.creditQuestions?.includes('CCJs') && {
            CCJsAcceptable: 'Include',
          }),
          ...(values?.creditQuestions?.includes('IVAs') && {
            IVAsAcceptable: 'Include',
          }),
          ...(values?.creditQuestions?.includes('Arrears') && {
            ArrearsAcceptable: 'Include',
          }),
        }),
        // ...(values?.others?.includes('Credit Problems') && {
        //   CCJsAcceptable: 'Include',
        //   IVAsAcceptable: 'Include',
        //   BankruptcyAcceptable: 'Include',
        //   ArrearsAcceptable: 'Include',
        //   DefaultsAcceptable: 'Include',
        // }),
        ...(values?.fees === 'Free Mortgage Valuation' && {
          ValuationFee: 'Ignore',
        }),
        // ...(values?.others?.includes('Offset') && {
        //   IsOffet: 'Include',
        // }),
        // ...(values?.others?.includes('Flexible (No ERC)') && {
        //   NoERCPayable: 'Include',
        // }),
        // ...(values?.others?.includes('Help to Buy') && {
        //   HelpToBuy: 'Include',
        // }),
        IncludedPackagers: [
          {
            string: 'LENDER',
          },
        ],
      },
      NumberOfItems: 3000,
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getLender = (code: string) => {
    return filteredLenders?.find((lender) => lender.Code === code)
  }

  const getInitialRateDuration = (months: number) => {
    if (months <= 29) return 2
    if (months <= 40) return 3
    if (months <= 68) return 5

    return 10
  }

  // Function to handle form submission
  const onSubmit = async (values: FormSchema) => {
    if (isDrawerOpen && isMatchDownMd) {
      setDrawerToggle()
    }
    console.log(values)
    values = {
      ...values,
      propertyValue: _.floor(values.propertyValue || 0),
      loanRequired: _.floor(values.loanRequired || 0),
      percentagePurchased: _.ceil(values.percentagePurchased || 0),
      shareValue: _.floor(values.shareValue || 0),
      rent: _.floor(values.rent || 0),
      discountedPurchasePrice: _.floor(values.discountedPurchasePrice || 0),
      term: _.floor(values.term || 0),
    }
    await mutateAsync({
      runSourceArgument: getRunSourceArgument(values),
      apiKey: theme?.custom?.apiKey,
    })
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sortProducts = (products: Product[], sortedBy: string) => {
    return products.sort(
      (a, b) =>
        (((a as any)[sortedBy]! as number) - (b as any)[sortedBy]!) as number
    )
  }

  const filterUniqueProducts = (products: Product[]) => {
    let uniqueProducts: Product[] = []
    let seen = new Set()

    products.forEach((product) => {
      let totalFees = (
        product.ProcFee +
        product.BookingFee +
        product.LegalFee +
        product.BrokerFee +
        product.ValuationFee +
        product.DeedsReleaseFee +
        product.DisbursementFee +
        product.FeesTotal
      ).toLocaleString('en-GB', {
        minimumFractionDigits: 2,
      })

      let key = `${product.LenderCode}-${product.InitialPayRate}-${totalFees}`

      if (!seen.has(key)) {
        seen.add(key)
        uniqueProducts.push(product)
      }
    })

    return uniqueProducts
  }

  React.useEffect(() => {
    if (filteredLenders?.length) {
      const findLender =
        filteredLenders.find(
          (lender) =>
            lender.Name.toLocaleLowerCase() ===
            config?.lender?.toLocaleLowerCase()
        )?.Code ?? ''

      form.setValue('lender', findLender)

      mutateAsync({
        runSourceArgument: getRunSourceArgument(
          modifyFormValues(form?.getValues()!)
        ),
        apiKey: theme?.custom?.apiKey,
      })
    }
  }, [lenders])

  React.useEffect(() => {
    try {
      const list =
        (products || []).map((product) => ({
          ...product,
          LenderImageUrl: getLender(product.LenderCode)?.SmallImageSecureUrl!,
          InitialRateDuration: getInitialRateDuration(
            product.InitialRatePeriodMonths
          ),
        })) || []
      setProductsList(filterUniqueProducts(list))
    } catch (err) {
      setProductsList([])
    }
  }, [products])

  // Value to be provided by the context
  const contextValue: FormContextValue = {
    form,
    productsList: [...sortProducts(productsList, sortedBy)],
    lenders: filteredLenders || [],
    isFetchingLenders: isFetching,
    isFetchingProducts: isPending,
    isFetchingProductsSuccess: isSuccess,
    onSubmit,
  }

  return (
    <FormContext.Provider value={contextValue}>
      <Box
        id="mp-best-buy-mortgage-form"
        className="mp-best-buy-slide"
        component="form"
        onSubmit={form.handleSubmit(onSubmit)}
        noValidate
      >
        <HookProvider {...form}>{children}</HookProvider>
      </Box>
    </FormContext.Provider>
  )
}
